/* Warehouse Bold — the two shipping estimators.
 *
 *   DELIVERY ESTIMATE (inline, in the How-to-get-it card): dates first,
 *   price as secondary text. The quick answer.
 *
 *   SHIPPING ESTIMATE (drawer, from "Estimate now ›"): prices first, dates
 *   as secondary text, with the cart context — item line, subtotal, and the
 *   free-shipping progress bar. The breakdown.
 *
 * Both run on ONE engine — one postcode, one transit fetch, one date
 * computation, one price path — so they can never disagree about the same
 * destination. The postcode entered in either is remembered and auto-runs
 * the other.
 *
 * ── Division of truth ──────────────────────────────────────────────────────
 *  · TRANSIT ranges come from the Cloud Function proxy (Australia Post's
 *    delivery-times API — the same source as auspost.com.au's calculator).
 *    `express` may be null; the row is dropped, never guessed.
 *  · DISPATCH is computed here from Brisbane wall-clock time + the cutoff +
 *    WBHolidays (which knows the Gold Coast Show). Dates are never cached —
 *    only ranges are — so an overnight cache can't freeze a date.
 *  · PRICES come ONLY from Shopify's own rate engine — never a table in
 *    this file. The store runs several delivery profiles (General 9.99 /
 *    12.70 by weight, Road Transport 12.95 with no express, flat 12.95 and
 *    9.99 profiles, EDMO weight tiers, Boeing dropship), so a flat
 *    fallback is not imprecise, it is WRONG: a 12.95-profile item sitting
 *    in the cart once priced a 9.99-profile product page at $12.95.
 *
    ONE engine, any scope: Storefront API `cartCreate` builds a THROWAWAY
 *    cart holding whatever set of lines is being priced — this product
 *    alone (buy box), the cart, or the cart plus this product (modal) —
 *    and reads its delivery options. Tokenless and same-origin (verified
 *    live 2026-08-09: Novus → $9.99, Dykem → $12.95, qty 8 → $0.00).
 *
 *    /cart/shipping_rates.json is gone: it can only quote the cart exactly
 *    as it stands, so the modal's "your cart plus this item" priced the
 *    cart alone. One engine also means one rate-selection rule — the two
 *    tools cannot disagree about the same rates.
 *
 *    ⚠ The throwaway cart must NEVER carry buyerIdentity.email or .phone
 *    and must set oneTimeUse — those are what create abandoned-checkout
 *    records. Verified clean: ~40 test carts produced none. Never navigate
 *    to its checkoutUrl, never persist its id, never touch the real cart.
 *    ⚠ Ignore carrier-calculated rates (withCarrierRates/@defer): not
 *    available on this plan, and Owen removed the one such method.
 *  · The drawer lists EVERY line the quote covers — the quote is
 *    cart-scope, and a subtotal over invisible items reads as a wrong
 *    number (Owen, 2026-08-09: $45.75 against a single $29.50 line).
 */
(() => {
  const parseBlob = (sel) => {
    const el = document.querySelector(sel);
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  };

  const init = () => {
    /* TWO blobs, one config. The shared facts (endpoint, cutoff, threshold,
       currency, common copy) are rendered once in <head> for every page,
       because the cart drawer is global; the product page adds a second blob
       holding only what is product-scoped. Merging beats duplicating: two
       copies of the endpoint or the threshold is two answers to one question.

       ⚠ They MUST keep different attribute names. The product blob is found
       with querySelector — first match in document order — so a head-level
       blob sharing the attribute would shadow it and take drawerId, variantId
       and priceCents with it, killing the PDP modal silently. */
    const shared = parseBlob('[data-wb-ship-config]');
    if (!shared || !window.WBHolidays) return;
    const prod = parseBlob('[data-wb-est-config]');
    const cfg = Object.assign({}, shared, prod || {});
    cfg.t = Object.assign({}, shared.t || {}, (prod && prod.t) || {});
    if (!cfg.endpoint) return; // no proxy, no promises — the widget stays inert
    const H = window.WBHolidays;
    const T = cfg.t || {};

    /* Product widgets need product facts. Without them (any non-product page)
       only the cart-drawer widget runs. Guarding on the blob rather than on
       page type keeps the rule where the data is. */
    const hasProduct = !!(prod && prod.variantId);
    const inline = hasProduct ? document.querySelector('[data-wb-est]') : null;
    const drawer = hasProduct ? document.getElementById(cfg.drawerId || 'WbDrawerShipcost') : null;

    /* ---------------- shared state ---------------- */

    /* ⚠ The in-memory mirror is not an optimisation. In private mode / with
       storage blocked, localStorage.setItem throws and getItem returns
       nothing — so a postcode the shopper had just typed read back as "no
       postcode", which hid the field AND the Change control and left them
       with no way to enter one again. The page-lifetime value is authoritative
       whenever it exists. */
    const PC_KEY = 'wb:postcode';
    let pcMemo = '';
    const getPostcode = () => {
      if (pcMemo) return pcMemo;
      try { return localStorage.getItem(PC_KEY) || ''; } catch (e) { return ''; }
    };
    const setPostcode = (pc) => {
      pcMemo = pc;
      try { localStorage.setItem(PC_KEY, pc); } catch (e) { /* private mode */ }
    };

    const esc = (v) => String(v == null ? '' : v).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
    const money = (cents) => '$' + (cents / 100).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    /* The theme prints the ISO code beside cart money whenever
       currency_code_enabled is on. Estimator money follows the same setting,
       so one panel can never show "$9.99" next to the theme's "$29.50 AUD".
       Two functions on purpose: money() for textContent, moneyHTML() where the
       code needs its own smaller, quieter span. */
    const CUR = esc(cfg.currency || '');
    const moneyHTML = (cents) => (cfg.showCurrency && CUR
      ? money(cents) + ' <span class="wb-cur">' + CUR + '</span>'
      : money(cents));

    const lang = document.documentElement.lang || 'en';
    const locale = lang === 'en' ? 'en-AU' : lang;
    const fmtDate = (d) => d.toLocaleDateString(locale, { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });

    /* ⚠ Origin-aware. This used to be Brisbane-only, which is correct for
       Arundel and an hour or two wrong for EDMO (Adelaide: +9:30, and +10:30
       under daylight saving). `cfg.originTz` arrives per product from Liquid;
       the cart drawer has no product context and stays on the shop default. */
    const ORIGIN = cfg.origin || 'main';
    const ORIGIN_TZ = cfg.originTz || 'Australia/Brisbane';
    const ORIGIN_PC = cfg.originPostcode || '';
    const CAL = (H && H.for) ? H.for(ORIGIN) : H;
    const brisbaneNow = () => {
      const parts = new Intl.DateTimeFormat('en-AU', {
        // ⚠ hourCycle h23, and a %24 belt: bare hour12:false lets some
        // engines emit hour "24" for midnight, which read as after-cutoff
        // and rolled dispatch a day late between midnight and 1am.
        timeZone: ORIGIN_TZ, year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', hourCycle: 'h23'
      }).formatToParts(new Date()).reduce((o, p) => { o[p.type] = p.value; return o; }, {});
      return { year: +parts.year, month: +parts.month, day: +parts.day, hour: (+parts.hour) % 24 };
    };

    /* Dates are computed fresh on every render — never stored. */
    const datesFor = (range) => {
      const dispatch = CAL.nextDispatch(brisbaneNow(), cfg.cutoffHour || 15);
      const from = CAL.addBusinessDays(dispatch, range[0]);
      const to = CAL.addBusinessDays(dispatch, range[1]);
      return from.getTime() === to.getTime() ? fmtDate(from) : fmtDate(from) + ' – ' + fmtDate(to);
    };

    /* ---------------- transit + suburb (the proxy) ---------------- */

    const fetchJSON = (url) => {
      const ctl = new AbortController();
      const timer = setTimeout(() => ctl.abort(), 8000);
      return fetch(url, { signal: ctl.signal })
        .then((r) => {
          if (!r.ok) throw Object.assign(new Error('HTTP ' + r.status), { status: r.status });
          return r.json();
        })
        .finally(() => clearTimeout(timer));
    };

    const fetchEstimate = (pc) => {
      // Session-cache RANGES per postcode per Brisbane day. Dates recompute.
      const day = (() => { const n = brisbaneNow(); return n.year + '-' + n.month + '-' + n.day; })();
      /* ⚠ ORIGIN is in the key. Without it the first product a shopper
         prices fixes that postcode's range for every other product in the
         session — an Arundel range would render on an EDMO page with no
         network call at all, and Brisbane differs by five days. */
      const key = 'wb:est:' + ORIGIN + ':' + pc + ':' + day;
      try {
        const hit = sessionStorage.getItem(key);
        if (hit) return Promise.resolve(JSON.parse(hit));
      } catch (e) { /* private mode */ }
      return Promise.all([
        fetchJSON(cfg.endpoint + '/delivery-estimate?to=' + pc + (ORIGIN_PC ? '&from=' + encodeURIComponent(ORIGIN_PC) : '')),
        fetchJSON(cfg.endpoint + '/postcode?q=' + pc).catch(() => ({ suburb: null }))
      ]).then(([ranges, place]) => {
        const out = { standard: ranges.standard, express: ranges.express || null, suburb: place.suburb || null };
        try { sessionStorage.setItem(key, JSON.stringify(out)); } catch (e) { /* full */ }
        return out;
      });
    };

    /* ⚠ &v= is a CACHE BUSTER, not a feature flag. The proxy answers with
       Cache-Control: max-age=86400, so when the international response shape
       changed from {standard:[a,b]} to {services:{…}} every shopper who had
       used the estimator in the previous day kept getting the old shape for
       another 24 hours — and the new renderer read it as "Not available" for
       their country. Bump this whenever that shape changes. */
    const INTL_SHAPE = 2;
    /* The origin matters internationally too, not just domestically: Australia
       Post publishes +2 business days (Express) and +5 (Standard) for items
       leaving from SA, TAS or NT, and EDMO is Salisbury South. Without this
       every international estimate on a DS1 product understated by 2-5 days. */
    const fetchIntl = (code) =>
      fetchJSON(cfg.endpoint + '/delivery-estimate?country=' + code +
        '&from=' + encodeURIComponent(cfg.originPostcode || '') + '&v=' + INTL_SHAPE);

    /* Every destination Australia Post carries. Fetched once per page from the
       proxy — never rendered into the theme — and memoised per page load so
       reopening the picker costs nothing. */
    let countriesPromise = null;
    const fetchCountries = () => {
      if (!countriesPromise) {
        countriesPromise = fetchJSON(cfg.endpoint + '/countries')
          .then((d) => (d && d.countries) || [])
          .catch((e) => { countriesPromise = null; throw e; }); // let a retry happen
      }
      return countriesPromise;
    };

    /* ---------------- prices (Shopify's checkout engine) ---------------- */

    // Postcode → state, because /cart/shipping_rates.json refuses to quote
    // without a province. First-digit bands plus the ACT carve-outs.
    const PROVINCE_CODES = {
      'Australian Capital Territory': 'ACT', 'Northern Territory': 'NT',
      'New South Wales': 'NSW', 'Victoria': 'VIC', 'Queensland': 'QLD',
      'South Australia': 'SA', 'Western Australia': 'WA', 'Tasmania': 'TAS',
    };
    const provinceCodeFor = (pc) => PROVINCE_CODES[provinceFor(pc)] || 'QLD';
    const provinceFor = (pc) => {
      const n = +pc;
      if ((n >= 200 && n <= 299) || (n >= 2600 && n <= 2618) || (n >= 2900 && n <= 2920)) return 'Australian Capital Territory';
      if (n >= 800 && n <= 999) return 'Northern Territory';
      if (n >= 1000 && n <= 2999) return 'New South Wales';
      if ((n >= 3000 && n <= 3999) || (n >= 8000 && n <= 8999)) return 'Victoria';
      if ((n >= 4000 && n <= 4999) || (n >= 9000 && n <= 9999)) return 'Queensland';
      if (n >= 5000 && n <= 5999) return 'South Australia';
      if (n >= 6000 && n <= 6999) return 'Western Australia';
      if (n >= 7000 && n <= 7999) return 'Tasmania';
      return 'Queensland';
    };

    let cartState = { subtotal: 0, count: 0, thisVariantCents: 0, inCart: false, lines: [], codes: [], stale: false };
    const refreshCart = () => fetch('/cart.js')
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((c) => {
        const lines = (c.items || []).map((i) => ({
          title: i.product_title || i.title || '',
          qty: i.quantity,
          image: i.image || null,
          cents: i.final_line_price != null ? i.final_line_price : i.line_price || 0,
          variantId: i.variant_id,
        }));
        /* ⚠ Guarded on hasProduct, not on a truthy cfg.variantId. Off a
           product page variantId is undefined, and `i.variant_id === undefined`
           is false for every line — which reads as "not in the cart" and is
           accidentally right, but the same undefined reaches
           quoteLines as 'gid://…/undefined' and priceCents * qty as NaN. Being
           explicit here is what keeps the cart widget honest. */
        const mine = hasProduct
          ? lines.filter((i) => i.variantId === cfg.variantId).reduce((sum, i) => sum + i.cents, 0)
          : 0;
        /* Discount codes ride along to the throwaway quote cart. Without them
           the quote prices an UNDISCOUNTED basket, so a discounted cart sitting
           just under the free-shipping threshold gets told "+ FREE shipping"
           and is charged at checkout. */
        const codes = (c.discount_codes || [])
          .filter((d) => d && d.applicable !== false && d.code)
          .map((d) => d.code);
        cartState = {
          subtotal: c.total_price, count: c.item_count, thisVariantCents: mine,
          inCart: hasProduct && lines.some((i) => i.variantId === cfg.variantId),
          lines, codes, stale: false,
        };
        return cartState;
      })
      /* ⚠ Flagged, not swallowed. Returning the previous snapshot silently
         quoted a cart that no longer exists — the shopper sees a price for
         lines they may have just removed. Callers check `stale`. */
      .catch(() => { cartState = Object.assign({}, cartState, { stale: true }); return cartState; });

    /* The rate card: base standard + express, in cents.
       Preference order: a real quote from this cart → the last real quote
       this browser ever saw → the theme settings. A real quote only teaches
       us the standard BASE price when the cart is under the threshold
       (above it Shopify already discounts to 0 — which is fine, because
       then the combined subtotal is over too and we show FREE). */
    /* ⚠ Tier classification decides WHICH transit dates a quoted service may
       wear, and getting it wrong is a false delivery promise. Our dates come
       from Australia Post's network for a parcel leaving 4214 — so they are
       only valid for an Australia Post service.

       The live store proves a bare /express/i test is not safe: the Boeing
       Dropship profile's only methods are named "TNT Road Express" ($15 and
       $0 over the threshold), and the Road Transport profile ships aerosols
       as "Standard Road Freight". Bucketing those as Auspost Express put a
       road-freight price against a next-business-day promise on 90 live
       variants, and rendered a bold FREE next-day claim over $199.

       So: a service that is not Australia Post gets its real name and price
       and NO date. Owen can supply indicative road-freight days later; until
       then an absent date is the honest answer. */
    const tierFor = (title) => {
      const t = String(title || '');
      if (/tnt|road\s*freight|road\s*express|courier|freight/i.test(t)) return null; // not AusPost
      if (/express/i.test(t)) return 'express';
      return 'standard';
    };

    /* One reducer for BOTH quote sources, so the drawer and the buy box can
       never disagree about the same rates. Cheapest wins per distinct
       service, because several profiles return near-duplicate names
       ("Standard" alongside "Standard Delivery (Auspost)") and the
       free-over-$199 rate arrives as a separate method that co-matches. */
    const toServices = (opts, getName, getAmount) => {
      const byKey = {};
      opts.forEach((o) => {
        const label = String(getName(o) || '').trim();
        if (!label) return;
        const cents = Math.round(parseFloat(getAmount(o)) * 100);
        if (!Number.isFinite(cents) || cents < 0) return;
        const tier = tierFor(label);
        // Same tier collapses to one row; untiered services stay distinct.
        const key = tier || ('other:' + label.toLowerCase());
        const prev = byKey[key];
        if (!prev || cents < prev.cents) byKey[key] = { label, cents, tier };
      });
      const list = Object.keys(byKey).map((k) => byKey[k]);
      if (!list.length) return null;
      // Standard, then express, then anything else — cheapest first within.
      const rank = (x) => (x.tier === 'standard' ? 0 : x.tier === 'express' ? 1 : 2);
      list.sort((a, b) => rank(a) - rank(b) || a.cents - b.cents);
      return list;
    };

    /* ---- Product-scope quote: Storefront API, throwaway cart ----
       The only way to price THIS product alone without touching the
       visitor's cart. Same-origin and tokenless today; cfg.storefrontToken
       is honoured if Shopify ever requires one, so no code change is
       needed then. */
    const SF_QUERY = 'mutation Q($input:CartInput!){cartCreate(input:$input){userErrors{message}cart{deliveryGroups(first:10){nodes{deliveryOptions{title code deliveryMethodType estimatedCost{amount currencyCode}}}}}}}';

    /* ⚠ Delivery GROUPS are shipments, not alternatives. When a cart splits
       across shipping origins Shopify returns one group per shipment and the
       shopper pays one option from EACH — so flattening every group's options
       into one list and taking the cheapest under-quotes by exactly the other
       shipments' cost. (Pilot Gear ships nearly everything from Arundel, so a
       single group is the common case, but Boeing dropship items do not.)

       Rule: a service is only offerable when EVERY group can provide it, and
       its price is the sum of the cheapest match in each group. A tier one
       group cannot serve is dropped rather than shown at a partial price. */
    const combineGroups = (groups) => {
      const perGroup = groups
        .map((opts) => toServices(opts, (o) => o.title, (o) => o.estimatedCost && o.estimatedCost.amount))
        .filter(Boolean);
      // A group with no rate at all means the destination is not served.
      if (!perGroup.length || perGroup.length !== groups.length) return null;
      if (perGroup.length === 1) return perGroup[0];

      const keyOf = (sv) => sv.tier || ('other:' + sv.label.toLowerCase());
      const out = [];
      for (const candidate of perGroup[0]) {
        const key = keyOf(candidate);
        let total = 0;
        let servedByAll = true;
        for (const group of perGroup) {
          const matches = group.filter((sv) => keyOf(sv) === key);
          if (!matches.length) { servedByAll = false; break; }
          total += Math.min.apply(null, matches.map((sv) => sv.cents));
        }
        if (servedByAll) out.push({ label: candidate.label, cents: total, tier: candidate.tier });
      }
      return out.length ? out : null;
    };
    /* ⚠ Namespaced BY SHAPE. The payload changed from {standard,express} to
       a service list, and entries cached by the previous deploy silently
       failed the new shape test — prices vanished while dates stayed. Bump
       this whenever the cached structure changes. */
    /* ⚠ v4: the payload now carries the SHIPMENT COUNT alongside the service
       list, so the modal can say "ships in 2 parcels" — a split basket is the
       whole reason its total can exceed the sum a shopper expects. A property
       hung on the array would not survive JSON.stringify, hence the wrapper. */
    const PQ_NS = 'wb:pq:v4:';
    const productQuoteCache = {};
    /* Quotes ANY set of lines: this product alone (buy box), the cart, or
       the cart plus this product. One engine for every scope, so the two
       tools can never apply different rules to the same rates — and the
       modal's "your cart plus this item" copy is now literally what was
       quoted. Replaces /cart/shipping_rates.json, which could only ever
       quote the cart exactly as it stands. */
    const quoteLines = (pc, lines, codes) => {
      const codeList = (codes || []).slice().sort();
      // ⚠ Codes are part of the key: the same lines quote differently once a
      // discount moves the basket across the free-shipping threshold.
      const key = lines.map((l) => l.id + 'x' + l.q).join(',') + '|' + pc + '|' + codeList.join('+');
      if (productQuoteCache[key]) return productQuoteCache[key];
      try {
        const hit = sessionStorage.getItem(PQ_NS + key);
        if (hit) {
          const saved = JSON.parse(hit);
          const list = saved.s;
          if (list) list.groups = saved.g;
          productQuoteCache[key] = Promise.resolve(list);
          return productQuoteCache[key];
        }
      } catch (e) { /* private mode */ }
      const ctl = new AbortController();
      const timer = setTimeout(() => ctl.abort(), 9000);
      const headers = { 'Content-Type': 'application/json' };
      if (cfg.storefrontToken) headers['X-Shopify-Storefront-Access-Token'] = cfg.storefrontToken;
      productQuoteCache[key] = fetch('/api/' + (cfg.sfVersion || '2026-07') + '/graphql.json', {
        method: 'POST', headers, signal: ctl.signal,
        body: JSON.stringify({
          query: SF_QUERY,
          variables: { input: {
            lines: lines.map((l) => ({ merchandiseId: 'gid://shopify/ProductVariant/' + l.id, quantity: l.q })),
            buyerIdentity: { countryCode: 'AU' },
            // Free-shipping thresholds are evaluated after discounts, so a
            // quote without the cart's codes can promise FREE on a basket the
            // discount has pushed back under the threshold.
            discountCodes: codeList,
            // ⚠ deliveryAddress is a nested wrapper — flattening it is the
            // classic cause of permanently empty deliveryGroups. validationStrategy
            // defaults to COUNTRY_CODE_ONLY, which is what lets a bare
            // country+postcode quote at all.
            delivery: { addresses: [{ address: { deliveryAddress: { countryCode: 'AU', provinceCode: provinceCodeFor(pc), zip: pc } }, selected: true, oneTimeUse: true }] },
          } },
        }),
      })
        .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
        .then((j) => {
          const cart = j && j.data && j.data.cartCreate && j.data.cartCreate.cart;
          const nodes = (cart && cart.deliveryGroups && cart.deliveryGroups.nodes) || [];
          const groups = nodes.map((g) => g.deliveryOptions || []).filter((o) => o.length);
          if (!groups.length) return null; // no rate covers that destination
          const card = combineGroups(groups);
          if (!card) return null;
          // How many separate shipments this basket makes. 1 for the ordinary
          // case; 2+ when lines come from stock held in different places, which
          // is what doubles the price and needs saying out loud.
          card.groups = groups.length;
          try { sessionStorage.setItem(PQ_NS + key, JSON.stringify({ s: card, g: groups.length })); } catch (e) { /* full */ }
          return card;
        })
        .catch(() => { delete productQuoteCache[key]; return null; })
        .finally(() => clearTimeout(timer));
      return productQuoteCache[key];
    };

    const quoteProduct = (pc, n) => quoteLines(pc, [{ id: cfg.variantId, q: n }]);

    /* Quote-or-nothing. Keyed by postcode AND the cart snapshot: the quote
       is address-parameterised and cart-dependent. Returns null when there
       is nothing real to quote (empty cart) or the quote fails — callers
       render dates without prices. */
    const qtyInput = document.querySelector('form[data-type="add-to-cart-form"] [name="quantity"], [name="quantity"]');
    const qty = () => Math.max(1, parseInt(qtyInput && qtyInput.value, 10) || 1);

    /* ---------------- rendering ---------------- */

    const rowsHTML = (est, card, pricesFirst) => {
      /* With a quote we render the services the store ACTUALLY offers, under
         their own names — that is what checkout will show. Without one we
         fall back to the two Australia Post tiers as dates only. Either way
         a row appears only when it has something true to say. */
      /* A recognised Australia Post tier wears the design's short label
         ("Standard" / "Express") — "Standard Delivery (Auspost)" wraps in
         the buy box and says nothing extra. Anything we could NOT map keeps
         its real name, because that name is the only thing telling the
         shopper why it has no date and what checkout will call it. */
      const services = (card && card.length)
        ? card.map((sv) => ({
            key: sv.tier,
            label: sv.tier === 'standard' ? T.standard : sv.tier === 'express' ? T.express : sv.label,
            cents: sv.cents,
          }))
        : [{ key: 'standard', label: T.standard, cents: null }, { key: 'express', label: T.express, cents: null }];
      let h = '';
      for (const sv of services) {
        const key = sv.key;
        const label = sv.label;
        const range = key ? est[key] : null;
        // A non-Australia-Post service carries no AusPost date; a tier with
        // no transit range carries none either. Price alone is still useful.
        if (!range && sv.cents == null) continue;
        const cents = sv.cents;
        const price = cents === 0 ? '<b class="wb-est__free">' + T.free + '</b>'
          : cents == null ? '' : moneyHTML(cents);
        const dates = range ? datesFor(range) : '';
        h += pricesFirst
          ? '<div class="wb-shipq__row"><div><div class="wb-shipq__svc">' + label + '</div>' +
            (dates ? '<div class="wb-shipq__dates">' + T.est_prefix + ' ' + dates + '</div>' : '') + '</div>' +
            '<div class="wb-shipq__price">' + price + '</div></div>'
          : '<div class="wb-est__row"><span class="wb-est__svc">' + label + '</span>' +
            '<span class="wb-est__dates">' + dates + '</span>' +
            '<span class="wb-est__price">' + price + '</span></div>';
      }
      return h;
    };

    const errFor = (err) => (err && err.status === 400 ? T.err_postcode : T.err_outage);

    /* ---------------- inline widget ---------------- */

    if (inline) {
      const input = inline.querySelector('[data-wb-est-input]');
      const btn = inline.querySelector('[data-wb-est-check]');
      const out = inline.querySelector('[data-wb-est-out]');
      const domesticBox = inline.querySelector('[data-wb-est-domestic]');
      const intlBox = inline.querySelector('[data-wb-est-intl]');
      const intlSel = inline.querySelector('[data-wb-est-country]');
      const intlOut = inline.querySelector('[data-wb-est-intl-out]');

      /* ⚠ Every async paint is stamped. The 400ms debounce cancels pending
         TIMERS, never in-flight requests, and a cached quote resolves in a
         microtask while a live one takes ~1s — so responses genuinely land
         out of order. Unguarded, stepping 1→8→1 let the qty-8 response
         (FREE over $199) repaint a qty-1 buy box as FREE. Only the newest
         issued request may write. */
      let inlineSeq = 0;
      let lastInline = null; // {est, card, pc} — repainted when qty moves
      const paintInline = () => {
        if (!lastInline) return;
        const { est, card, pc } = lastInline;
        const place = est.suburb ? est.suburb + ' (' + pc + ')' : pc;
        const html = rowsHTML(est, card, false) +
          '<p class="wb-est__zone">' + T.zone_note.replace('@@P@@', place) + '</p>';
        // Identical repaints are skipped: this is an aria-live region, and
        // rewriting unchanged markup re-announces the whole estimate on
        // every qty keystroke.
        if (out.innerHTML !== html) out.innerHTML = html;
      };
      const runInline = (pc) => {
        const seq = ++inlineSeq;
        out.innerHTML = '<p class="wb-est__hint">' + T.loading + '</p>';
        /* PRODUCT scope: a throwaway Storefront cart holding only this
           variant, so the buy box shows this product's own profile price
           with nothing in the visitor's cart. */
        Promise.all([fetchEstimate(pc), quoteProduct(pc, qty())])
          .then(([est, card]) => {
            if (seq !== inlineSeq) return; // superseded
            lastInline = { est, card, pc };
            paintInline();
          })
          .catch((err) => {
            if (seq !== inlineSeq) return;
            lastInline = null;
            out.innerHTML = '<p class="wb-est__err">' + errFor(err) + '</p>';
          });
      };
      /* Quantity is priced, not just displayed — weight bands and the
         $199 threshold both move with it — so the stepper re-quotes.
         Debounced because a quote is a network round trip (~1s) and the
         stepper fires per click; repeats hit the session cache. */
      let qtyT = null;
      if (qtyInput) ['change', 'input'].forEach((ev) => qtyInput.addEventListener(ev, () => {
        clearTimeout(qtyT);
        qtyT = setTimeout(() => {
          if (!lastInline) return;
          const seq = ++inlineSeq;
          const pc = lastInline.pc;
          const n = qty();
          quoteProduct(pc, n).then((card) => {
            // Stale on two axes: a newer request exists, or the shopper has
            // moved the stepper again since this one was issued.
            if (seq !== inlineSeq || n !== qty() || !lastInline || lastInline.pc !== pc) return;
            lastInline.card = card;
            paintInline();
          });
        }, 400);
      }));

      const submitInline = () => {
        const pc = (input.value || '').trim();
        if (!/^\d{4}$/.test(pc)) {
          out.innerHTML = '<p class="wb-est__err">' + T.err_postcode + '</p>';
          input.setAttribute('aria-invalid', 'true');
          return;
        }
        input.removeAttribute('aria-invalid');
        setPostcode(pc);
        runInline(pc);
      };

      btn.addEventListener('click', submitInline);
      input.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); submitInline(); } });
      input.addEventListener('input', () => { input.removeAttribute('aria-invalid'); });

      // International mode: ranges only, never dates — customs makes a date
      // promise dishonest.
      inline.querySelectorAll('[data-wb-est-mode]').forEach((link) => {
        link.addEventListener('click', (e) => {
          e.preventDefault();
          const intl = link.dataset.wbEstMode === 'intl';
          domesticBox.hidden = intl;
          intlBox.hidden = !intl;
        });
      });
      /* Renders Australia Post's PUBLISHED per-country windows.
         ⚠ Availability is per SERVICE. Courier exists for only 23 of the 227
         destinations and Express is suspended for 47 of them, so a service
         may legitimately have no window while the others do — and the pricing
         API will still quote a Courier PRICE for a country that has no
         Courier service. Never fill a gap with a number. */
      const showIntl = (code, name) => {
        intlOut.innerHTML = '<p class="wb-est__hint">' + T.loading + '</p>';
        fetchIntl(code)
          .then((data) => {
            /* Tolerate the pre-v2 shape as well. A version bump fixes the
               browser cache, but a CDN or a proxy in front of a shopper can
               hold a response longer than we control — and degrading to the
               old two-service answer beats telling them their country is not
               served. */
            const legacy = (r) => (Array.isArray(r) ? { min: r[0], max: r[1] } : null);
            const svc = (data && data.services) || (data && (data.standard || data.express)
              ? { express: legacy(data.express), standard: legacy(data.standard) }
              : {});
            const when = (s) => {
              if (!s) return null;
              if (s.status === 'suspended') return { txt: T.svc_suspended, off: true };
              if (s.status === 'unavailable') return { txt: T.svc_unavailable, off: true };
              if (typeof s.min !== 'number') return null;
              /* max null is AusPost's open-ended "15+", not a missing value. */
              return {
                txt: s.max == null
                  ? (T.bd_min || '').replace('@@MIN@@', s.min)
                  : (T.bd_range || '').replace('@@MIN@@', s.min).replace('@@MAX@@', s.max),
                off: false,
              };
            };
            const row = (label, key) => {
              const w = when(svc[key]);
              if (!w) return '';
              return '<div class="wb-est__row' + (w.off ? ' is-off' : '') + '">' +
                '<span class="wb-est__svc">' + label + '</span>' +
                '<span class="wb-est__dates">' + w.txt + '</span>' +
                '<span class="wb-est__price"></span></div>';
            };
            /* ⚠ Courier is deliberately NOT offered (Owen, 2026-08-14). It
               exists to only 23 of the 227 destinations, so it was absent far
               more often than present, and it is not a service this store
               actually sells — quoting a transit time for something a shopper
               cannot buy is worse than silence. The proxy still returns it;
               the decision not to show it lives here. */
            const rows = row(T.express, 'express') + row(T.standard, 'standard');
            const note = data && data.source === 'generic'
              ? '<p class="wb-est__zone">' + (T.generic_note || '') + '</p>' : '';
            /* ⚠ The destination is stated in words, above the numbers, not
               left implicit in the picker. A shopper on a VPN, or on a market
               they did not choose, otherwise reads a confident set of dates
               for the wrong country and never questions it (Owen,
               2026-08-14). Mirrors the domestic "Estimated delivery to
               Melbourne (3000)". */
            const to = name
              ? '<p class="wb-est__to">' + (T.intl_to || '').replace('@@PLACE@@', name) + '</p>'
              : '';
            /* Australia Post carries it out; the shopper's own post office
               carries it in. Overseas buyers track with the latter. */
            const carrier = '<p class="wb-est__zone">' + (T.carrier_note || '') + '</p>';
            intlOut.innerHTML = to + (rows || '<p class="wb-est__hint">' + (T.svc_unavailable || '') + '</p>') +
              note + carrier + '<p class="wb-est__zone">' + T.customs + '</p>';
          })
          .catch((err) => { intlOut.innerHTML = '<p class="wb-est__err">' + errFor(err) + '</p>'; });
      };

      /* ---------------- country type-ahead ----------------
         A <select> of 227 options is unusable on a phone, so this is a real
         combobox: input + listbox, filtered as you type.
         ⚠ Option labels go in via textContent, never innerHTML — the names
         come from an upstream API, and one day one of them will contain an
         ampersand or a quote. */
      if (intlSel) {
        const box = intlSel.closest('[data-wb-country]');
        const list = box && box.querySelector('[data-wb-country-list]');
        let all = [];
        let shown = [];
        let active = -1;
        let chosen = '';

        const close = () => {
          list.hidden = true;
          intlSel.setAttribute('aria-expanded', 'false');
          intlSel.removeAttribute('aria-activedescendant');
          active = -1;
        };

        const paint = () => {
          list.textContent = '';
          if (!shown.length) {
            const li = document.createElement('li');
            li.className = 'wb-country__empty';
            li.textContent = T.country_none || '';
            list.appendChild(li);
          } else {
            shown.forEach((c, i) => {
              const li = document.createElement('li');
              li.className = 'wb-country__opt';
              li.id = intlSel.id + '-o' + i;
              li.setAttribute('role', 'option');
              li.setAttribute('aria-selected', i === active ? 'true' : 'false');
              li.dataset.code = c.code;
              li.textContent = c.name;
              /* mousedown, not click: the input's blur would close the list
                 before a click ever landed. */
              li.addEventListener('mousedown', (e) => { e.preventDefault(); pick(i); });
              list.appendChild(li);
            });
          }
          list.hidden = false;
          intlSel.setAttribute('aria-expanded', 'true');
        };

        const filter = (q) => {
          const s = q.trim().toLowerCase();
          shown = !s
            ? all.slice(0, 400)
            : all
                .filter((c) => c.name.toLowerCase().indexOf(s) !== -1 || c.code.toLowerCase() === s)
                /* Prefix matches first: typing "in" should offer India before
                   Argentina, which merely contains it. */
                .sort((a, b) => {
                  const ap = a.name.toLowerCase().indexOf(s) === 0 ? 0 : 1;
                  const bp = b.name.toLowerCase().indexOf(s) === 0 ? 0 : 1;
                  return ap - bp || a.name.localeCompare(b.name, 'en');
                });
          active = shown.length ? 0 : -1;
          paint();
        };

        const pick = (i) => {
          const c = shown[i];
          if (!c) return;
          chosen = c.code;
          intlSel.value = c.name;
          close();
          showIntl(c.code, c.name);
        };

        /* The list is a network call on first open. Without this the field
           looked inert for however long that took — nothing opened, nothing
           said why. */
        const loading = () => {
          list.textContent = '';
          const li = document.createElement('li');
          li.className = 'wb-country__empty';
          li.textContent = T.loading || '';
          list.appendChild(li);
          list.hidden = false;
          intlSel.setAttribute('aria-expanded', 'true');
        };

        let loadingNow = false;
        const load = () => {
          if (loadingNow) return;
          loadingNow = true;
          loading();
          return fetchCountries()
            .then((cs) => { all = cs; filter(intlSel.value === chosenName() ? '' : intlSel.value); })
            .catch(() => {
              close();
              intlOut.innerHTML = '<p class="wb-est__err">' + (T.country_err || '') + '</p>';
            })
            .then(() => { loadingNow = false; });
        };

        const chosenName = () => {
          const hit = all.find((c) => c.code === chosen);
          return hit ? hit.name : '\u0000';
        };

        intlSel.addEventListener('focus', () => { all.length ? filter('') : load(); });
        intlSel.addEventListener('input', () => {
          chosen = '';
          all.length ? filter(intlSel.value) : load();
        });
        intlSel.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            if (list.hidden) { all.length ? filter(intlSel.value) : load(); return; }
            if (!shown.length) return;
            active = (active + (e.key === 'ArrowDown' ? 1 : -1) + shown.length) % shown.length;
            paint();
            const el = list.children[active];
            if (el) { el.scrollIntoView({ block: 'nearest' }); intlSel.setAttribute('aria-activedescendant', el.id); }
          } else if (e.key === 'Enter') {
            if (!list.hidden && active >= 0) { e.preventDefault(); pick(active); }
          } else if (e.key === 'Escape') {
            close();
          }
        });
        intlSel.addEventListener('blur', () => {
          /* Leaving the field with text that matched nothing would otherwise
             sit there looking chosen. */
          setTimeout(() => { if (!chosen) intlSel.value = ''; close(); }, 120);
        });
      }

      /* Outside Australia the international estimate is the only mode, so it
         opens already answered for the market the shopper is actually in.
         ⚠ Guarded on marketIsAu rather than on the country code: the two can
         disagree (an Australian browsing the German storefront), and the
         market is what the page is priced and shipped for. */
      if (!cfg.marketIsAu && cfg.market && intlSel) {
        fetchCountries()
          .then((cs) => {
            const hit = cs.find((c) => c.code === String(cfg.market).toUpperCase());
            if (!hit) return;
            intlSel.value = hit.name;
            showIntl(hit.code, hit.name);
          })
          .catch(() => {});
      }

      const saved = getPostcode();
      if (saved && input) { input.value = saved; runInline(saved); }
    }

    /* ---------------- the drawer ---------------- */

    if (drawer) {
      const input = drawer.querySelector('[data-wb-shipq-input]');
      const btn = drawer.querySelector('[data-wb-shipq-go]');
      const out = drawer.querySelector('[data-wb-shipq-out]');
      const atcBtn = drawer.querySelector('[data-wb-shipq-atc]');
      const footEl = drawer.querySelector('[data-wb-shipq-foot]');

      /* The modal shows rates, a sentence saying what they cover, and Add to
         cart. Nothing else.

         It used to list the cart, total it, and carry a free-shipping bar.
         The total was the trap: it was cart PLUS this unadded item, under a
         label ("Subtotal") that every shopper reads as "my cart" — $67.00
         against a $29.50 cart. And the three blocks together pushed ADD TO
         CART off the bottom of the modal. The free-shipping bar now exists in
         exactly one place, the cart drawer, where the number behind it is the
         cart and only the cart. */
      const paintCartBits = () => {
        const hasCart = cartState.count > 0;
        const inCart = cartState.inCart;
        if (atcBtn) atcBtn.hidden = inCart; // already there — a second button invites double-adding
        // One sentence, three truths: this item alone, the cart, or both.
        /* ⚠ The rows are THIS ITEM in every state now, so the footnote no
           longer switches with cart scope — it described the old basket-priced
           headline. The basket has its own labelled line instead. */
        if (footEl) footEl.textContent = T.foot_item;
      };

      /* Secondary line: what the WHOLE order costs, and why it differs.
         ⚠ Says "parcels", never where they ship from. Nothing customer-facing
         names a supplier or a location. */
      const orderLineHTML = (itemCard, orderCard) => {
        if (!cartState.count || !orderCard || !orderCard.length) return '';
        const money = (tier) => {
          const hit = orderCard.find((sv) => sv.tier === tier);
          if (!hit) return '';
          return '<span class="wb-shipq__ord-svc">' +
            (tier === 'standard' ? T.standard : T.express) + ' ' +
            (hit.cents === 0 ? '<b class="wb-est__free">' + T.free + '</b>' : moneyHTML(hit.cents)) +
            '</span>';
        };
        const prices = money('standard') + money('express');
        if (!prices) return '';
        /* Only worth a line when the order actually costs more than this item
           on its own — otherwise it repeats the headline in smaller type. */
        const itemStd = itemCard && itemCard.find((sv) => sv.tier === 'standard');
        const ordStd = orderCard.find((sv) => sv.tier === 'standard');
        if (itemStd && ordStd && itemStd.cents === ordStd.cents) return '';
        const parcels = orderCard.groups > 1
          ? '<span class="wb-shipq__ord-note">' + T.parcels.replace('@@N@@', orderCard.groups) + '</span>'
          : '';
        return '<div class="wb-shipq__order"><div class="wb-shipq__ord-head">' + T.with_cart + '</div>' +
          '<div class="wb-shipq__ord-body">' + prices + parcels + '</div></div>';
      };

      const runDrawer = (pc) => {
        const seq = ++drawerSeq;
        out.innerHTML = '<p class="wb-est__hint">' + T.loading + '</p>';
        /* TWO quotes, because the modal now answers two questions in the order
           a shopper actually asks them.

           ⚠ The headline is THIS ITEM alone — it used to be the whole basket,
           and that is what made the modal look broken: it read $24.94 beside a
           buy box reading $14.95 for the same product, with the explanation
           buried in a footnote (Owen, 2026-08-16). Both numbers were right;
           only one of them was the question being asked. The item price is
           also the one that matches the buy box, so the page stops appearing
           to contradict itself.

           The basket total has not gone away — it moved to its own line below,
           where it is labelled and carries the parcel count that explains it.
           It is worth having: it IS what checkout will charge, verified
           against /cart/shipping_rates.json for the same basket. */
        const itemLines = [{ id: cfg.variantId, q: qty() }];
        const orderLines = cartState.lines.map((l) => ({ id: l.variantId, q: l.qty }));
        if (!cartState.inCart) orderLines.push({ id: cfg.variantId, q: qty() });
        const itemPriced = quoteLines(pc, itemLines, cartState.codes);
        const orderPriced = cartState.count && orderLines.length
          ? quoteLines(pc, orderLines, cartState.codes)
          : Promise.resolve(null);
        Promise.all([fetchEstimate(pc), itemPriced, orderPriced])
          .then(([est, card, orderCard]) => {
            if (seq !== drawerSeq) return; // superseded
            lastDrawer = { est, card };
            paintCartBits(); // prices may shift once the threshold is crossed
            out.innerHTML = rowsHTML(est, card, true) + orderLineHTML(card, orderCard);
          })
          .catch((err) => {
            if (seq !== drawerSeq) return;
            lastDrawer = null;
            out.innerHTML = '<p class="wb-est__err">' + errFor(err) + '</p>';
          });
      };

      let drawerPc = '';
      const submitDrawer = () => {
        const pc = (input.value || '').trim();
        if (!/^\d{4}$/.test(pc)) {
          out.innerHTML = '<p class="wb-est__err">' + T.err_postcode + '</p>';
          input.setAttribute('aria-invalid', 'true');
          return;
        }
        input.removeAttribute('aria-invalid');
        setPostcode(pc);
        drawerPc = pc;
        runDrawer(pc);
        // Keep the two tools in step without re-fetching.
        const other = inline && inline.querySelector('[data-wb-est-input]');
        if (other && !other.value) other.value = pc;
      };

      btn.addEventListener('click', submitDrawer);
      input.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); submitDrawer(); } });

      // The trigger opens the dialog via wb-drawer.js; this listener rides
      // the same click to refresh the cart context and auto-run.
      document.querySelectorAll('[data-wb-drawer="' + drawer.id + '"]').forEach((t) => {
        t.addEventListener('click', () => {
          // SEQUENCED: pricing from the pre-click cart showed "qualifies
          // for FREE" beside a Standard row still priced from the old
          // subtotal when the cart had changed in another tab or drawer.
          refreshCart().then(() => {
            paintCartBits();
            const saved = getPostcode();
            if (saved) { input.value = saved; drawerPc = saved; runDrawer(saved); }
          });
        });
      });

      let drawerSeq = 0;
      let lastDrawer = null; // {est, card} — repainted when qty moves
      const paintDrawerRows = () => {
        if (!lastDrawer) return;
        const html = rowsHTML(lastDrawer.est, lastDrawer.card, true);
        if (out.innerHTML !== html) out.innerHTML = html; // aria-live: no identical repaints
      };
      if (qtyInput) ['change', 'input'].forEach((ev) => qtyInput.addEventListener(ev, () => { paintCartBits(); paintDrawerRows(); }));

      if (atcBtn) atcBtn.addEventListener('click', () => {
        atcBtn.disabled = true;
        fetch('/cart/add.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: cfg.variantId, quantity: qty() }),
        })
          .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
          .then(() => {
            // The theme's own refresh hook — keeps the header count and the
            // cart drawer in step without reimplementing either.
            document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
            return refreshCart();
          })
          .then(() => {
            paintCartBits();
            // Scope just changed from product to cart, so the quote must
            // re-run: adding can cross the free threshold or combine
            // profiles into a different rate.
            if (drawerPc) runDrawer(drawerPc);
          })
          .catch(() => { out.innerHTML = '<p class="wb-est__err">' + T.err_add + '</p>'; })
          .finally(() => { atcBtn.disabled = false; });
      });
      refreshCart().then(paintCartBits);
    }

    /* ---------------- the cart drawer ----------------

       The third surface, and the only one that exists on every page. Same
       engine, cart scope: it quotes exactly the lines in the cart, never the
       product being viewed, so the drawer answers "what will THIS cost to
       ship" and the buy box answers "what will THAT cost" without either
       having to know about the other.

       ⚠ Everything inside #CartDrawerFooter has its innerHTML replaced
       wholesale by cart.js on every add / remove / quantity change, and the
       replacement comes from a fresh Liquid render. So this block cannot hold
       state in the DOM and cannot keep a listener alive across a cart change:
       it is re-mounted on `cart:updated`, and the only state that survives is
       module-level (the postcode, which also lives in localStorage, and any
       half-typed draft).

       That churn is a feature for correctness: a cart change invalidates the
       quote anyway, so being torn down is exactly when we should re-ask. */

    let cartDraft = '';   // a half-typed postcode, so a qty change mid-entry does not eat it
    let cartSeq = 0;
    /* A WeakSet of roots we have already bound, NOT a single "last mounted"
       reference. cart:updated also fires for changes that do not replace the
       footer (cart:refresh with an unchanged section, another drawer's
       update), and a version that cleared its reference on every event
       re-bound the SAME nodes each time — every listener, and every quote,
       duplicated per cart change. */
    const cartBound = new WeakSet();

    const mountCart = () => {
      const root = document.querySelector('[data-wb-cq]');
      if (!root || cartBound.has(root)) return;
      cartBound.add(root);

      const input = root.querySelector('[data-wb-cq-input]');
      const go = root.querySelector('[data-wb-cq-go]');
      const ask = root.querySelector('[data-wb-cq-ask]');
      const change = root.querySelector('[data-wb-cq-change]');
      const place = change && change.querySelector('[data-wb-cq-place]');
      const out = root.querySelector('[data-wb-cq-out]');
      if (!input || !go || !out || !ask) return;

      /* Per-ROOT date suppression, set by Liquid when the cart holds a line
         with no truthful arrival date (the DS2 rule — those parts move by a
         different carrier than the one this estimator models). Price still
         quotes; rowsHTML simply never sees a transit range. Read from the
         root so the drawer's own estimate keeps its behaviour, and re-read
         naturally on every cart:updated re-render because the root is new. */
      const noDates = root.hasAttribute('data-wb-cq-nodates');
      const noDatesNote = root.getAttribute('data-wb-cq-nodates-note') || '';

      /* out is an aria-live region: writing identical markup re-announces the
         whole estimate, and this block repaints on every quantity change. */
      const say = (html) => { if (out.innerHTML !== html) out.innerHTML = html; };

      const showAsk = (on) => {
        ask.hidden = !on;
        // ⚠ getPostcode(), which now falls back to a page-lifetime value.
        // Reading localStorage alone meant a blocked store hid the field AND
        // the Change control — no way back to entering a postcode.
        if (change) change.hidden = on || !getPostcode();
      };

      const run = (pc) => {
        const seq = ++cartSeq;
        say('<div class="wb-cq__hint">' + esc(T.loading) + '</div>');
        refreshCart().then((state) => {
          if (seq !== cartSeq) return;
          // A failed /cart.js leaves the previous snapshot in place; pricing it
          // would quote lines the shopper may have just removed.
          if (state.stale) { say('<div class="wb-cq__err">' + esc(T.err_outage) + '</div>'); return; }
          // An empty cart has nothing to quote, and the theme hides this whole
          // footer anyway. Never invent a rate for zero lines.
          if (!state.count) { say(''); return; }
          const lines = state.lines.map((l) => ({ id: l.variantId, q: l.qty }));
          return Promise.all([fetchEstimate(pc), quoteLines(pc, lines, state.codes)]).then(([est, card]) => {
            if (seq !== cartSeq) return;
            const where = est.suburb ? est.suburb + ' (' + pc + ')' : pc;
            if (change) change.hidden = false;
            if (place) place.textContent = est.suburb ? est.suburb + ' ' + pc : pc;
            // A half-typed postcode outranks a resolving quote: collapsing the
            // field under someone's cursor is the worse failure. So is
            // collapsing it out from under the keyboard — if focus is inside
            // the field, it moves to the control that reopens it.
            if (!cartDraft) {
              const hadFocus = ask.contains(document.activeElement);
              showAsk(false);
              if (hadFocus && change && !change.hidden) change.focus();
            }
            say(rowsHTML(noDates ? {} : est, card, true) +
              (noDates && noDatesNote ? '<p class="wb-cq__zone">' + esc(noDatesNote) + '</p>' : '') +
              '<p class="wb-cq__zone">' + T.zone_note.replace('@@P@@', esc(where)) + '</p>');
          });
        }).catch((err) => {
          if (seq !== cartSeq) return;
          say('<div class="wb-cq__err">' + esc(errFor(err)) + '</div>');
        });
      };

      const submit = () => {
        const pc = (input.value || '').trim();
        if (!/^\d{4}$/.test(pc)) {
          // Bump the sequence so an in-flight quote cannot land on top of
          // this message.
          cartSeq++;
          say('<div class="wb-cq__err">' + esc(T.err_postcode) + '</div>');
          input.setAttribute('aria-invalid', 'true');
          return;
        }
        input.removeAttribute('aria-invalid');
        cartDraft = '';
        setPostcode(pc);
        run(pc);
        // The product page's own field, if we are on one, adopts the postcode
        // rather than asking again — one postcode, both tools.
        const other = inline && inline.querySelector('[data-wb-est-input]');
        if (other && !other.value) other.value = pc;
      };

      go.addEventListener('click', submit);
      input.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); submit(); } });
      input.addEventListener('input', () => { cartDraft = input.value; input.removeAttribute('aria-invalid'); });
      if (change) change.addEventListener('click', () => {
        showAsk(true);
        input.value = getPostcode();
        input.focus();
        input.select();
      });

      const start = () => {
        const saved = getPostcode();
        if (saved) {
          input.value = cartDraft || saved;
          if (place) place.textContent = saved;
          showAsk(!!cartDraft);
          run(saved);
        } else {
          input.value = cartDraft;
          showAsk(true);
          say('<div class="wb-cq__hint">' + esc(T.cart_hint) + '</div>');
        }
      };
      cartRerun = start;
      start();
    };

    /* cart.js swaps the footer's innerHTML and THEN dispatches cart:updated,
       so by the time this fires the new nodes are already in place — and a
       replaced root is a root the WeakSet has never seen, which is what makes
       mountCart re-bind. If the nodes happened to survive, the listeners are
       still good but the quote is not: the cart just changed, so re-run it. */
    let cartRerun = null;
    document.addEventListener('cart:updated', () => {
      const root = document.querySelector('[data-wb-cq]');
      if (root && cartBound.has(root)) { if (cartRerun) cartRerun(); return; }
      mountCart();
    });
    mountCart();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
